import { QuestionsService } from '../services/questions-service';
import { UserService } from '../services/user-service';
import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators';
import { Events } from '../events/app-events';
import { DepInjMixin } from '../mixins/dependency';
import { LazyLoadService } from '../services/lazyload-service';
import { defer } from '../utils/async-store';
import { eventBus } from '../utils/event-bus';
import { DeviceAwareComponent } from './components/device-aware-component';
import { LoginMode } from '../services/user-service';
import { classMap } from 'lit/directives/class-map';
import { ApiService } from '@APP/services/api-service';
import { ConfigService } from '@APP/services/config-service';
import { tokens } from '../styles/tokens';
import './app-menu/app-menu';
import { WebSectionTrackingData } from '@FLAVOR/../../common/types';
import { getQueryParam } from '@ahoy/aquarium/dist/utils/getQueryParam';

type Deps = {
  questionsService: QuestionsService;
  userService: UserService;
  apiService: ApiService;
  configService: ConfigService;
};


/**
 * @class
 */
@customElement('main-view')
export class MainView extends DepInjMixin<Deps>(DeviceAwareComponent) {
  // This use of static inject is an edge-case of a class decorator running AFTER the initialization of an instance
  // This is a known issue with javascript compilers. The reason for the late decoration is that the main-view is already on-stage before
  // the application code runs and defines it. The browser is quick enough to run the lifecycle hooks BEFORE the decorators run.
  static get inject() {
    return [QuestionsService.serviceName, UserService.serviceName, ApiService.serviceName, ConfigService.serviceName];
  }
  static get styles() {
    return [
      css`
        :host {
          display: flex;
          justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
        position: relative;
      }

      :host([loading-state="landing-page"]) {
        opacity: 0;
      }
    `,
    ];
  }

  static get mobileStyles() {
    return [
      tokens,
      css`
      app-menu {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
    }
    .background-header {
      position: absolute;
      background: linear-gradient(180deg, #F6F6F9 100%, rgba(246, 246, 249, 0) 100%);
      width: 100vw;
      height: 23.75rem;
      top: 0;
      z-index: 1;
    }
    .path-top-header {
      position: absolute;
      width: 100vw;
      height: 23.75rem;
      top: 0;
      z-index: 1;
      filter: drop-shadow(0px 5px 25px #19489226);
      background-image: url(./assets/mobile_wave.svg);
      background-position: center bottom;
      background-repeat: no-repeat;
      background-size: 100%; 
    }
    :host([current-section="S_Summary_Details"]) {
        --question-wrapper-justify-content: center;
      --block-width: calc(100vw - 2 * var(--block-margin));
    }
    :host([current-section="S_MTA_Coverages"]) {
        --question-wrapper-justify-content: center;
      --block-width: calc(100vw - 2 * var(--block-margin));
    }
    :host([current-section="S_Additional_Boaters"]) .path-top-header,
    :host([current-section="Congratulations"]) .path-top-header {
      height: 16.25rem;
    }
    :host([current-section="S_Additional_Boaters"]) .background-header,
    :host([current-section="Congratulations"]) .background-header {
      height: 16.25rem;
    }
:host([current-section="S_Billing"]) {
      --block-margin:2.75rem;
    }
      [keyboard-open] .path-top-header {
        height: 12rem;  
      }
      
      [keyboard-open] .background-header {
        height: 0;
      }
    `,
    ];
  };

  static get desktopStyles() {
    return [
      css`
        :host(.login-page) {
          background-image: url(./assets/login-bg.svg);
          background-position: right top;
          background-repeat: no-repeat;
        background-size: contain;
      }
      
      lh-login-view {
        justify-content: flex-start;
      }
      
    :host([new-landing-page="true"]){
      align-items:unset;
    }

    :host([current-section="S_Welcome"]) [currentblockidx="2"] + .OtpIllustration {
      right: 0;
      z-index: 1;
      position: absolute;
      width: 100%;
      height: 100vh;
      background-image: url(./assets/login-bg.svg);
      background-position: right top;
      background-repeat: no-repeat;
      background-size: contain;
    }
      :host([current-section="S_Billing"]),
      :host([current-section="S_Payment"]){
        --layout-background: url('./assets/bg_payment.svg');
      }
      
    .BgIllustration {
      right: 0;
      z-index: 1;
      position: absolute;
      width: 100%;
      height: 100vh;
      background-image: url(./assets/bg_form.svg);
      background-position: right center;
      background-repeat: no-repeat;
      background-size: contain;
    }
      
      .layout-content {
        --group-title-bg: transparent;
        --grid-spacing: repeat(6, minmax(0, 1fr));
      }
      .layout-content {
        background-image: var(--layout-background,url('./assets/bg_additional.svg'));
        background-position: center bottom;
        background-repeat: no-repeat;
        background-size: 100%;
        
      }
    `,
    ];
  };

  firstUpdated() {
    /** Sending web tracking data */
    this.sendWebtrackingData();
  }

  connectedCallback() {
    super.connectedCallback();
    eventBus().on(Events.Type.SectionChanged, (s) => { this.setAttribute('current-section', s.sectionId); });
    eventBus().on<string>(Events.Type.Refresh, (status) => {
      this.status = status;
      LazyLoadService.loadLogoutRestartButtons().then(() => {
        this.isLoggedIn = true;
      });
    });
    eventBus().on(Events.Type.NavigatingOutOfNewLp, () => {
      this.newLandingPage = false;
      this.setAttribute('loading-state', 'questionnaire');
    });
    eventBus().on(Events.Type.LogExitOnBackgroundSend, (backgroundSendData) => {
      const op = backgroundSendData?.op || '';
      this.backgroundSend(op);
    });
    eventBus().on(Events.Type.Congratulations, ({ congrats }) => {
      this.setAttribute('current-section', 'Congratulations');
      this.deps.apiService.unlock();
      this.congrats = congrats;
    });
    eventBus().on(Events.Type.SectionChanged, (s) => { this.section = s; });
    eventBus().on(Events.Type.CustomLpLoad, () => {
      document.body.setAttribute('new-landing-page', 'true');
      this.newLandingPage = true;
      this.setAttribute('loading-state', 'landing-page-view');
    });
    eventBus().on(Events.Type.GoToWebsite, (param) => {
      const url = param?.url || this.deps.configService.get('websiteUrl');
      if (url) {
        this.internalReload = !param?.alert;
        window.location.assign(url);
      }
    });
    // we check if this is an existing application, since on refresh we want the main-view to show even
    // though we have a SRC value.
    // eslint-disable-next-line no-empty-pattern
    eventBus().on<any>(Events.Type.QuestionnaireIdSet, ({}) => { this.setAttribute('loading-state', 'questionnaire'); });
    // we also want to display the main-view on any non-landing page SRC value
    eventBus().on<any>(Events.Type.RemoveFunnelHiding, () => { this.setAttribute('loading-state', 'questionnaire'); });

    window.addEventListener('load', function() { window.history.pushState({}, ''); });
    this.back = this.back.bind(this);
    window.addEventListener('popstate', this.back);
    window.addEventListener('beforeunload', this.beforeunload);
    window.addEventListener('unload', this.unload);
    document.addEventListener('visibilitychange', this.userleave);
    defer('device-detect').then(() => this.appReady = true);
    const { isLandingPage } = this.handleQueryParams();
    LazyLoadService.loadPostWelcomeAssets();
    this.deps.userService.device_type = this.userDevice;
    this.deps.userService.saveOtpTrackingLoad();
    /** Send S_Welcome data before login to gtm */
    this.deps.userService.sendWebtrackingData('load', '');
    if (!this.deps.userService.isLoggedIn) {
      this.deps.userService.autoLogin({ isLandingPage, isMobile: this.userDevice === 'mobile' });
    }
  }

  private resolveDefer(value: string) {
    defer('src').resolve(value);
  }

  private handleQueryParams() {
    if (getQueryParam('MODE') == 'login') {
      this.deps.userService.loginMode = 'login';
      this.loginMode = 'login';
    }
    
    if (getQueryParam('user_mode') == 'payment') {
      this.deps.questionsService.setPaymentLinkMode(true);
    }

    const srcValue = getQueryParam('SRC');
    const isExistingAffiliateValue = this.deps.questionsService?.model?.getQuestionById('Affiliate')?.value;
    let affiliateValue = getQueryParam('affiliate') || isExistingAffiliateValue as string;
    const srcCondition = srcValue === 'yamaha';

    if (srcCondition) {
      affiliateValue = srcValue;
    }

    const affiliateCondition = this.deps.questionsService.isAffiliateExist(affiliateValue);

    if (affiliateCondition) {
      this.deps.questionsService.affiliateService?.setAffiliate(affiliateValue);
      this.resolveDefer(affiliateValue);
    } else {
      this.resolveDefer('');
    }

    if (getQueryParam('fromCheckout') == '1') {
      this.deps.questionsService.isFromCCpayment = true;
    }
    if (srcValue && srcValue != 'site') {
      this.setAttribute('loading-state', 'landing-page');
      return { isLandingPage: true };
    }
    else {
      this.setAttribute('loading-state', 'questionnaire');
      return { isLandingPage: false };
    };
  }

  async sendWebtrackingData() {
    let data: WebSectionTrackingData;
    data = this.prepareTrackingData();
    eventBus().emit(Events.Type.SaveWebSectionTracking, data);
  };

  private prepareTrackingData = (): WebSectionTrackingData => {
    let data;

    data = {
      section: 'S_Welcome',
      next_section: '',
      operation: 'Arrival',
      errorMessage: '',
    };

    return data;
  };

  @state()
    internalReload = false;

  @state()
    section = '';

  @state()
    status = 'New';

  @state()
    isLoggedIn = false;

  @state()
    loginMode: LoginMode = 'signup';

  @state()
    congrats: boolean = false;

  @state()
    newLandingPage: boolean = false;

  @state()
    appReady: boolean = false;

  back() {
    eventBus().emit(Events.Type.Back);
  }

  private backgroundSend(op: string) {
    try {
      const qIdFromUrl = getQueryParam('questionnaireId');
      const analyticsData = {
        questionnaireId: this.deps.questionsService.questionnaireId || qIdFromUrl,
        op,
        section: this.congrats? 'congrats' : this.deps.questionsService.model.getActiveSectionName(),
        next_section: this.congrats?'login':'',
        device_type: this.deps.userService.device_type,
      };
      navigator.sendBeacon(this.deps.apiService.getAPIBaseUrl() + '/tracking/beacon', JSON.stringify(analyticsData));
    } catch (err) {
      console.error(err);
    }
  }

  beforeunload = (e: BeforeUnloadEvent) => {
    if (!this.internalReload) {
      e.preventDefault();
      this.backgroundSend('page-close-request');
      e.returnValue = 'alert';
    }
  };

  unload = () => {
    this.backgroundSend('exit');
  };

  userleave = () => {
    if (document.visibilityState === 'hidden') {
      this.backgroundSend('page-hidden');
    }
  };



  renderContent() {
    this.classList.remove('login-page');
    const isRenewal = this.deps.questionsService.isRenewal;
    if (!this.appReady) return html``;
    if (this.loginMode == 'login' && !this.deps.userService.isLoggedIn) { return this.renderLogin(); }
    else if (!isRenewal && (this.congrats || this.status == 'Payment')) {
      return this.renderCongrats();
    } else if (isRenewal || ['Incomplete', 'Additional Boat', 'New', 'Mem'].includes(this.status)) {
      return this.renderQuestionaire();
    } else return html``;
  }

  renderCongrats() {
    const wait = (ms: number) => setTimeout(() => { }, ms);
    // we wait 20ms to allow smooth loading
    wait(20);
    const layout = this.deps.questionsService.model.getCurrentSectionLayout();
    const isMobile = this.userDevice === 'mobile';

    return html`
    <popup-manager></popup-manager>

      <lh-app
      class=${classMap({
    'header-small': isMobile && layout === 'content',

  })}
      >
      ${isMobile ? html`
        <span class="path-top-header"></span>
        <lh-header class=${classMap({ hidebg: layout === 'content' })}>
          <div id="title-wrapper">
            <lh-app-header></lh-app-header>
          </div>
        </lh-header>
      ` : ''}
        <lh-congrats-view></lh-congrats-view>
      </lh-app>
      ${this.isLoggedIn
    ? (isMobile ? html`<app-menu></app-menu>` : html`<lh-logout-restart></lh-logout-restart><lh-terms-privacy></lh-terms-privacy>`)
    : nothing}
    `;
  }

  renderLogin() {
    this.classList.add('login-page');
    return html`
    <popup-manager></popup-manager>
      <lh-login-view></lh-login-view>
    `;
  }



  renderQuestionaire() {
    const active_section = this.deps.questionsService.model.getActiveSectionName();
    const layout = this.deps.questionsService.model.getCurrentSectionLayout();
    const otpIllustration = active_section === 'S_Welcome' ? html`<span class="OtpIllustration"></span>` : nothing;

    const no_bg_screens = ['S_Welcome', 'S_Boat', 'S_Pricing'];
    const bgIllustration = active_section && !no_bg_screens.includes(active_section) ? html`<span class="BgIllustration"></span>` : nothing;
    const isMobile = this.userDevice === 'mobile';

    let customStyle;
    if (active_section === 'S_Pricing') {
      customStyle = css`
      :host {
        --questions-grid-display: block;
        --questions-grid-width: 100%;
        --questions-wrapper-width: 100%;
      }
      `;
    } else {
      customStyle = nothing;
    }

    return html`
      <style>
        ${customStyle}
      </style>
      <lh-app
      class=${classMap({
    'layout-content': layout === 'content',
    'header-small': isMobile && layout === 'content',
    'new-landing-page': this.newLandingPage,
  })}
      >
    ${isMobile && !this.newLandingPage ? html`<span class="path-top-header"></span>` : '' }
      ${this.newLandingPage
    ? ''
    : html`
          <lh-header class=${classMap({ hidebg: layout === 'content' })}>
            <div id="title-wrapper">
              <lh-app-header></lh-app-header>
            </div>
          </lh-header>`
}

        ${this.userDevice === 'desktop'
    ? html`<lh-questionnaire style="z-index: 3" .isNewLandingPage="${this.newLandingPage}"></lh-questionnaire>`
    : html`<lh-questionnaire .isNewLandingPage="${this.newLandingPage}"></lh-questionnaire>`
}
        ${otpIllustration}
        ${layout != 'content' && this.userDevice === 'desktop' ? html`${bgIllustration}` : ''}

      </lh-app>
      
      ${this.isLoggedIn
    ? (isMobile ? html`<app-menu></app-menu>` : html`<lh-logout-restart></lh-logout-restart><lh-terms-privacy></lh-terms-privacy>`)
    : nothing}
      <popup-manager
      class=${classMap({
    'layout-popup': layout !== null,
  })}
      ></popup-manager>
    `;
  }
}
